/**
 * Which abilities are AIMED — the ones whose direction decides where they
 * land — derived from their hit shapes, never listed by name.
 *
 * This is the rule behind desktop hold-to-aim (InputManager): an aimed skill
 * shows its indicator on keydown and casts on keyup, so the player can see
 * the lane before committing; everything else casts on the press, because
 * there is nothing to aim. The founder's report was "i press a button and the
 * skill is released" — on a lane skill, with no time to look.
 *
 * Read off the same table the sim resolves with, so a new ability cannot be
 * forgotten here: a `dash_line`, a `projectile` and a `shot` all travel down
 * the aim; a `ground_zone` is aimed only if it lands DOWNRANGE (Starlink's 14
 * m offset) — a rune dropped at the caster's feet has no direction. A `self`
 * shape has nothing to aim, and a `sphere` is self-centred or auto-targets
 * (the chain, the ride pick the nearest body), so it fires on the press.
 *
 * The basic attack is never hold-to-aim whatever its shape: it is the click,
 * and holding it fires it.
 */

import { CHARACTERS } from '@chaos/protocol';
import { GROUND_ZONE_OFFSET } from '@chaos/sim';

/** @param {import('@chaos/protocol').AbilityDef | undefined} ability */
export function isAimedAbility(ability) {
  const hit = ability?.hit;
  if (!hit) return false;
  switch (hit.kind) {
    case 'dash_line':
    case 'projectile':
    case 'shot':
      return true;
    case 'ground_zone':
      return (GROUND_ZONE_OFFSET[ability.id] ?? 0) > 0;
    default:
      return false;
  }
}

/** The skill slots this character holds to aim: the aimed ones, the basic excluded. */
export function holdToAimSlots(characterId) {
  const def = CHARACTERS[characterId];
  const out = new Set();
  if (!def) return out;
  for (const slot of ['skill1', 'skill2', 'ultimate']) {
    if (isAimedAbility(def.abilities[slot])) out.add(slot);
  }
  return out;
}
